import { motion } from "framer-motion";
import { Wallet, Clock3, ShieldCheck, Check } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const features = [
  {
    icon: Wallet,
    accent: "text-brand-yellow",
    ring: "group-hover:border-brand-yellow/50",
    glow: "bg-brand-yellow/20",
    title: "Affordable Rides",
    copy: "Fares priced for Albany, not Manhattan. Lower fees mean riders save on every single trip — commutes, nights out, airport runs.",
    image:
      "https://images.unsplash.com/photo-1578041262130-633307b3bfd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBwYXNzZW5nZXIlMjBpbiUyMGNhcnxlbnwwfHx8fDE3ODkxNDI2MzN8MA&ixlib=rb-4.1.0&q=85",
    alt: "Passenger riding in a car",
    testid: "feature-affordable",
  },
  {
    icon: Clock3,
    accent: "text-brand-green",
    ring: "group-hover:border-brand-green/50",
    glow: "bg-brand-green/20",
    title: "Flexible Earnings",
    copy: "Drive mornings, weekends, or whenever life allows. No quotas, no lock-ins — just higher take-home pay on your own schedule.",
    image:
      "https://images.unsplash.com/photo-1521136095380-08fbd7be93c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxwZXJzb24lMjBwYXNzZW5nZXIlMjBpbiUyMGNhcnxlbnwwfHx8fDE3ODkxNDI2MzN8MA&ixlib=rb-4.1.0&q=85",
    alt: "Driver behind the wheel at golden hour",
    testid: "feature-flexible",
  },
];

const safetyPoints = [
  "Background-checked drivers",
  "Verified vehicles & insurance",
  "In-app trip tracking",
  "24/7 ride support",
];

export default function Features() {
  return (
    <section id="features" className="relative py-28 md:py-36 scroll-mt-20" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 md:mb-24 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div>
            <p className="text-xs font-bold tracking-[0.3em] uppercase text-zinc-500 mb-5">Why EzRueda</p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl max-w-2xl">
              Built different, <span className="text-brand-yellow">on purpose.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-sm text-sm md:text-base leading-relaxed">
            A local-first rideshare that puts money back in riders' pockets and drivers'
            hands — without cutting corners on safety.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.article
              key={f.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE, delay: i * 0.12 }}
              className={`group relative rounded-3xl border border-white/10 bg-brand-surface overflow-hidden transition-colors duration-500 ${f.ring}`}
              data-testid={f.testid}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={f.image}
                  alt={f.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-surface via-transparent to-transparent" />
                <div className={`absolute -bottom-10 left-1/2 -translate-x-1/2 w-56 h-56 ${f.glow} blur-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none`} />
              </div>
              <div className="p-8">
                <f.icon className={`w-7 h-7 ${f.accent} mb-5`} />
                <h3 className="font-display font-semibold text-2xl tracking-tight mb-3">{f.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{f.copy}</p>
              </div>
            </motion.article>
          ))}

          <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.24 }}
            className="group relative rounded-3xl border border-white/10 bg-gradient-to-b from-brand-elevated to-brand-surface overflow-hidden hover:border-white/30 transition-colors duration-500"
            data-testid="feature-safe"
          >
            <div className="p-8 flex flex-col h-full">
              <ShieldCheck className="w-7 h-7 text-white mb-5" />
              <h3 className="font-display font-semibold text-2xl tracking-tight mb-3">
                Safe &amp; Verified Drivers
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed mb-8">
                Every driver on EzRueda is screened, every vehicle inspected. Peace of mind
                comes standard on every trip.
              </p>
              <ul className="mt-auto space-y-3">
                {safetyPoints.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-zinc-300">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-brand-green/15">
                      <Check className="w-3 h-3 text-brand-green" />
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
